import React, { useState, useMemo } from 'react';
import { Combobox, ComboboxInput, ComboboxButton, ComboboxOptions, ComboboxOption } from '@headlessui/react';
import { MapPinIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

// ─── Location Combobox (R16-R17) ─────────────────────────────────────────────
// CAL-1 annotation 9 (CAL-01): Required combobox type-ahead searching
// previously-used addresses/job-site names. Allows free-text entry.

export default function LocationCombobox({ value, onChange, events = [], required = false }) {
  const [query, setQuery] = useState('');

  // Extract unique locations from existing events
  const suggestions = useMemo(() => {
    const locs = new Set();
    events.forEach(e => {
      if (e.location && e.location.trim()) locs.add(e.location.trim());
    });
    return Array.from(locs).sort();
  }, [events]);

  // Filter suggestions based on query
  const filtered = useMemo(() => {
    if (!query) return suggestions.slice(0, 8);
    const q = query.toLowerCase();
    return suggestions.filter(loc => loc.toLowerCase().includes(q)).slice(0, 8);
  }, [query, suggestions]);

  return (
    <Combobox value={value} onChange={(val) => onChange(val || '')} immediate>
      <div className="relative">
        <div className="relative">
          <MapPinIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <ComboboxInput
            className="input w-full pl-9 pr-16"
            placeholder="Enter address or job site"
            displayValue={(v) => v || ''}
            onChange={(e) => {
              setQuery(e.target.value);
              // Allow free-text entry: update parent on every keystroke
              onChange(e.target.value);
            }}
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {value && (
              <button
                type="button"
                onClick={() => { onChange(''); setQuery(''); }}
                className="p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                aria-label="Clear location"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            )}
            <ComboboxButton className="p-0.5 text-gray-400">
              <MagnifyingGlassIcon className="h-4 w-4" />
            </ComboboxButton>
          </div>
        </div>

        <ComboboxOptions className="absolute z-20 top-full mt-1 w-full bg-white border max-h-48 overflow-y-auto" style={{ borderColor: 'var(--color-border)', borderRadius: 'var(--r-base)' }}>
          {filtered.length === 0 && query && (
            <div className="px-3 py-2 text-xs text-gray-500">
              No saved locations — type a new one
            </div>
          )}
          {filtered.map((loc) => (
            <ComboboxOption
              key={loc}
              value={loc}
              className="px-3 py-2 text-sm text-gray-700 cursor-pointer data-[focus]:bg-gray-50 data-[selected]:font-medium truncate"
            >
              <div className="flex items-center gap-2">
                <MapPinIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                <span className="truncate">{loc}</span>
              </div>
            </ComboboxOption>
          ))}
        </ComboboxOptions>
      </div>
      {required && !value && (
        <p className="text-[10px] text-red-500 mt-0.5">Location is required</p>
      )}
    </Combobox>
  );
}
